import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../context/ThemeContext';
import {
  Users,
  UserCheck,
  DollarSign,
  CreditCard,
  TrendingUp,
  Activity
} from 'lucide-react';

const AnimatedStats = ({ stats }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const statCards = [
    {
      title: t('admin.totalUsers'),
      value: stats.totalUsers || 0,
      subtitle: t('admin.registeredMembers'),
      icon: Users,
      color: 'from-blue-500 to-cyan-500',
      change: '+12.5%'
    },
    {
      title: t('admin.activeUsers'),
      value: stats.activeUsers || 0,
      subtitle: t('admin.last30Days'),
      icon: UserCheck,
      color: 'from-green-500 to-emerald-500',
      change: '+8.2%'
    },
    {
      title: t('admin.totalCommissions'),
      value: `$${(stats.totalCommissions || 0).toLocaleString()}`,
      subtitle: t('admin.generatedRevenue'),
      icon: DollarSign,
      color: 'from-purple-500 to-pink-500',
      change: '+15.3%'
    },
    {
      title: t('admin.pendingPayouts'),
      value: `$${(stats.pendingPayouts || 0).toLocaleString()}`,
      subtitle: t('admin.toProcess'),
      icon: CreditCard,
      color: 'from-orange-500 to-red-500',
      change: '+5.1%'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
      {statCards.map((stat, index) => (
        <motion.div
          key={stat.title}
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ 
            type: "spring",
            stiffness: 300,
            damping: 30,
            delay: index * 0.1
          }}
          whileHover={{ 
            y: -5,
            transition: { duration: 0.2 }
          }}
          className="relative group"
        >
          <div className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl blur-xl -z-10"
               style={{ background: `linear-gradient(135deg, ${stat.color.split(' ')[1]}, ${stat.color.split(' ')[3]})` }}>
          </div>
          
          <div className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm group-hover:shadow-xl transition-all duration-300`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{stat.title}</p>
                <motion.p
                  className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} mb-2`}
                  key={stat.value}
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                >
                  {stat.value}
                </motion.p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{stat.subtitle}</p>

                <div className="flex items-center mt-3">
                  <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-sm font-medium text-green-600">
                    {stat.change}
                  </span>
                  <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'} ml-2`}>
                    {t('admin.vsLastMonth')}
                  </span>
                </div>
              </div>
              
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color} shadow-lg`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default AnimatedStats;